/**
 * <EmployeeDocumentsTab /> — the Documents tab on the Employee Master
 * File page (M21.8).
 *
 * Categorized document registry: upload (file + category + optional
 * expiry), list grouped by category, download, delete. Files go
 * through the BlobStore abstraction server-side (local FS → R2).
 *
 * A document expiring within 30 days gets a rose "Expires soon"
 * badge — same compliance-alert convention as the identity-expiry
 * fields on the Identity tab.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  FileText,
  Trash2,
  Upload,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError } from "../lib/api";
import type { EmployeeDocument } from "../lib/api";
import { Badge } from "./Badge";

const _CATEGORIES: EmployeeDocument["category"][] = [
  "contract",
  "passport",
  "id",
  "certificate",
  "bank",
  "other",
];

const _CATEGORY_LABEL_KEY: Record<EmployeeDocument["category"], string> = {
  contract: "app.edt_cat_contract",
  passport: "app.edt_cat_passport",
  id: "app.edt_cat_id",
  certificate: "app.edt_cat_certificate",
  bank: "app.edt_cat_bank",
  other: "app.edt_cat_other",
};

function _expiresSoon(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const expiry = new Date(dateStr).getTime();
  const in30 = Date.now() + 30 * 24 * 60 * 60 * 1000;
  return expiry <= in30;
}

function _fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function EmployeeDocumentsTab({
  employeeId,
}: {
  employeeId: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] =
    useState<EmployeeDocument["category"]>("contract");
  const [expiry, setExpiry] = useState("");
  const [notes, setNotes] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["employee-documents", employeeId],
    queryFn: () => api.listEmployeeDocuments(employeeId),
    enabled: !!employeeId,
  });

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["employee-documents", employeeId],
    });

  const uploadM = useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      if (expiry) fd.append("expiry_date", expiry);
      if (notes) fd.append("notes", notes);
      return api.uploadEmployeeDocument(employeeId, fd);
    },
    onSuccess: () => {
      setUploadError(null);
      setExpiry("");
      setNotes("");
      if (fileRef.current) fileRef.current.value = "";
      invalidate();
    },
    onError: (e: unknown) =>
      setUploadError(e instanceof ApiError ? e.detail : String(e)),
  });

  const deleteM = useMutation({
    mutationFn: (docId: string) =>
      api.deleteEmployeeDocument(employeeId, docId),
    onSuccess: invalidate,
  });

  const docs = q.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* Upload form */}
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
        <h3 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
          <Upload className="h-4 w-4" /> {t("app.edt_upload_h")}
        </h3>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="block text-sm">
            <span className="font-medium text-slate-700">{t("app.edt_category")}</span>
            <select
              value={category}
              onChange={(e) =>
                setCategory(
                  e.target.value as EmployeeDocument["category"],
                )
              }
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(_CATEGORY_LABEL_KEY[c])}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">
              {t("app.edt_expiry_opt")}
            </span>
            <input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">
              {t("app.edt_notes_opt")}
            </span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadM.mutate(f);
            }}
            className="text-sm"
          />
          {uploadM.isPending && (
            <span className="text-xs text-slate-500">{t("app.edt_uploading")}</span>
          )}
        </div>
        {uploadError && (
          <p className="mt-2 text-xs text-rose-700">{uploadError}</p>
        )}
      </div>

      {/* Document list grouped by category */}
      {docs.length === 0 ? (
        <p className="text-sm italic text-slate-500">
          {t("app.edt_no_docs_long")}
        </p>
      ) : (
        _CATEGORIES.filter((c) =>
          docs.some((d) => d.category === c),
        ).map((c) => (
          <div key={c}>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              {t(_CATEGORY_LABEL_KEY[c])}
            </h3>
            <table className="w-full border border-slate-200 text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-start">{t("app.edt_col_file")}</th>
                  <th className="px-3 py-2 text-start">{t("app.edt_col_size")}</th>
                  <th className="px-3 py-2 text-start">{t("app.edt_col_expiry")}</th>
                  <th className="px-3 py-2 text-start">{t("app.edt_col_uploaded")}</th>
                  <th className="px-3 py-2 text-end">{t("app.edt_col_actions2")}</th>
                </tr>
              </thead>
              <tbody>
                {docs
                  .filter((d) => d.category === c)
                  .map((d) => (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1.5">
                          <FileText className="h-4 w-4 text-slate-400" />
                          {d.filename}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">
                        {_fmtSize(d.file_size_bytes)}
                      </td>
                      <td className="px-3 py-2">
                        {d.expiry_date ? (
                          <span className="inline-flex items-center gap-1.5">
                            {d.expiry_date}
                            {_expiresSoon(d.expiry_date) && (
                              <Badge tone="rose">{t("app.edt_expires_soon")}</Badge>
                            )}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-500">
                        {d.created_at.slice(0, 10)}
                      </td>
                      <td className="px-3 py-2 text-end">
                        <a
                          href={api.employeeDocumentDownloadUrl(
                            employeeId,
                            d.id,
                          )}
                          target="_blank"
                          rel="noreferrer"
                          className="me-1 inline-flex rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                          aria-label={t("app.edt_dl_aria", { name: d.filename })}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        <button
                          type="button"
                          onClick={() => deleteM.mutate(d.id)}
                          className="inline-flex rounded p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                          aria-label={t("app.edt_del_aria", { name: d.filename })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
